import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap';
import Button from 'components/base/Button';
import HVAAyvensConversationWidget, {
  FeedbackMessage
} from './HVAAyvensConversationWidget';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faCar } from '@fortawesome/free-solid-svg-icons';

// Define vehicle information interface
interface VehicleInfo {
  licensePlate: string;
  mileage: number;
  serviceHistory?: string;
  tireSize?: string;
  serviceType?: 'maintenance' | 'tires';
  preferredArea?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  workshopName?: string;
  contactPhone?: string;
}

// Define the modal props
interface HVAAyvensModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  agentCode: string;
  agentName: string;
  agentImage?: string;
  onError?: (error: Error) => void;
  customer?: string;
  licensePlate?: string;
  mileage?: number;
  tireSize?: string;
  serviceHistory?: string;
  preferredArea?: string;
  contactPhone?: string;
  firstMessage: string;
  prompt: string;
  voice: string;
  language: string;
}

const HVAAyvensModal: React.FC<HVAAyvensModalProps> = ({
  show,
  onHide,
  title,
  agentCode,
  agentName,
  agentImage,
  onError,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customer,
  licensePlate,
  mileage,
  tireSize,
  serviceHistory,
  preferredArea,
  contactPhone,
  firstMessage,
  prompt,
  voice,
  language
}) => {
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>({
    licensePlate: licensePlate || 'XYZ1234',
    mileage: mileage || 29580,
    serviceHistory:
      serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
    tireSize: tireSize || '205/55R16',
    preferredArea: preferredArea || '',
    contactPhone: contactPhone || '694 555 1234'
  });

  const [formData, setFormData] = useState({
    licensePlate: licensePlate || 'XYZ1234',
    mileage: mileage?.toString() || '29580',
    tireSize: tireSize || '205/55R16',
    serviceHistory:
      serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
    preferredArea: preferredArea || '',
    contactPhone: contactPhone || '694 555 1234'
  });

  const [feedbackMessages, setFeedbackMessages] = useState<FeedbackMessage[]>(
    []
  );
  const [appointmentScheduled, setAppointmentScheduled] = useState(false);
  const [serviceTypeSelected, setServiceTypeSelected] = useState<string | null>(
    null
  );

  // Reset vehicle info when props change
  useEffect(() => {
    if (licensePlate && mileage) {
      setVehicleInfo({
        licensePlate: licensePlate,
        mileage: Number(mileage),
        serviceHistory:
          serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
        tireSize: tireSize || '205/55R16',
        preferredArea: preferredArea || '',
        contactPhone: contactPhone || '694 555 1234'
      });

      setFormData({
        licensePlate: licensePlate,
        mileage: mileage.toString(),
        tireSize: tireSize || '205/55R16',
        serviceHistory:
          serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
        preferredArea: preferredArea || '',
        contactPhone: contactPhone || '694 555 1234'
      });
    }
  }, [
    licensePlate,
    mileage,
    serviceHistory,
    tireSize,
    preferredArea,
    contactPhone
  ]);

  // Function to add feedback messages
  const addFeedbackMessage = (
    message: Omit<FeedbackMessage, 'id' | 'timestamp'>
  ) => {
    setFeedbackMessages(prev => [
      {
        ...message,
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date()
      },
      ...prev
    ]);
  };

  // Function to reset the state
  const resetState = () => {
    setFormData({
      licensePlate: licensePlate || 'XYZ1234',
      mileage: mileage?.toString() || '29580',
      tireSize: tireSize || '205/55R16',
      serviceHistory:
        serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
      preferredArea: preferredArea || '',
      contactPhone: contactPhone || '694 555 1234'
    });

    setVehicleInfo({
      licensePlate: licensePlate || 'XYZ1234',
      mileage: mileage || 29580,
      serviceHistory:
        serviceHistory || 'Due for 2nd Service and Emissions Card renewal',
      tireSize: tireSize || '205/55R16',
      preferredArea: preferredArea || '',
      contactPhone: contactPhone || '694 555 1234'
    });

    setFeedbackMessages([]);
    setAppointmentScheduled(false);
    setServiceTypeSelected(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setVehicleInfo({
      licensePlate: formData.licensePlate,
      mileage: Number(formData.mileage),
      serviceHistory: formData.serviceHistory,
      tireSize: formData.tireSize,
      preferredArea: formData.preferredArea,
      contactPhone: formData.contactPhone
    });

    setFeedbackMessages(prev => [
      {
        id: Math.random().toString(36).substring(2, 11),
        timestamp: new Date(),
        type: 'validation',
        message: 'Vehicle information updated',
        details: `License Plate: ${formData.licensePlate} .... Mileage: ${formData.mileage} km .... Tire Size: ${formData.tireSize}`,
        success: true
      },
      ...prev
    ]);
  };

  const handleCloseModal = () => {
    resetState();
    onHide();
  };

  const handleAppointmentScheduled = () => {
    setAppointmentScheduled(true);
    addFeedbackMessage({
      type: 'appointment',
      message: 'Appointment scheduled',
      details: `Service appointment scheduled for May 20th at 09:00 AM at AutoService Workshop. SMS confirmation will be sent to ${formData.contactPhone}.`,
      success: true
    });
  };

  const handleServiceTypeSelected = (type: string) => {
    setServiceTypeSelected(type);
    addFeedbackMessage({
      type: 'info',
      message: 'Service type selected',
      details: `Type: ${
        type === 'maintenance' ? 'Service Appointment' : 'Tire Order'
      }`,
      success: true
    });
  };

  const renderAppointmentAlert = () => {
    if (!appointmentScheduled) return null;

    return (
      <div className="d-flex align-items-center fs-9 mt-1 pt-2 bg-success-subtle p-2 rounded">
        <FontAwesomeIcon icon={faCalendarCheck} className="me-2 text-success" />
        <div className="flex-grow-1">Appointment Scheduled</div>
      </div>
    );
  };

  const renderServiceTypeAlert = () => {
    if (!serviceTypeSelected) return null;

    return (
      <div className="d-flex align-items-center fs-9 pt-2 bg-primary-subtle p-2 rounded">
        <FontAwesomeIcon icon={faCar} className="me-2 text-primary" />
        <div className="flex-grow-1">
          Service Type:{' '}
          {serviceTypeSelected === 'maintenance'
            ? 'Service Appointment'
            : 'Tire Order'}
        </div>
      </div>
    );
  };

  const renderFeedbackPanel = () => {
    return (
      <>
        <h5 className="pb-0 pt-4">{`${agentName}'s feedback`}</h5>
        <p className="fs-9 lh-sm">
          The panel below displays requests for information or tool usage made
          during the conversation.
        </p>
        <div
          className="p-3 pt-0 border rounded scrollbar bg-body-emphasis shadow-sm"
          style={{
            height: '150px',
            overflowY: 'auto',
            scrollBehavior: 'smooth'
          }}
        >
          {renderAppointmentAlert()}
          {renderServiceTypeAlert()}

          {/* Feedback messages */}
          <div className="mt-3">
            {feedbackMessages.map(msg => (
              <div
                key={msg.id}
                className={`alert ${
                  msg.success ? 'bg-body' : 'alert-warning'
                } mb-2 py-2 px-3`}
                role="alert"
              >
                <div className="d-flex flex-column">
                  <small className="text-muted fs-10">
                    {msg.timestamp.toLocaleTimeString()}
                  </small>
                  <div className="fs-9 text-primary lh-1">{msg.message}</div>
                  {msg.details && (
                    <small className="mt-1 text-muted fs-10 lh-1">
                      {msg.details}
                    </small>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      animation={true}
      contentClassName="border-0"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-body-emphasis">
          {`${title} - ${agentName}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 pe-3 bg-body">
        <Row className="g-0">
          <h3 className="pb-0 pt-4 px-6">Test scenario</h3>
          <p className="fs-8 lh-sm px-6 mb-0">
            This test scenario demonstrates the interaction between the agent,
            the customer, and the system for Ayvens vehicle service management.
            The agent starts the call without any prior information, which is
            provided incrementally as needed during the interaction.
          </p>

          {/* Left column: Initial info and test configuration */}
          <Col md={5} className="p-4 px-6 pe-0">
            <h5 className="pb-0 pt-2">Vehicle Data Configuration</h5>
            <p className="fs-9 lh-sm">
              Configure the vehicle information below to test different
              scenarios. The agent will validate this information during the
              conversation.
            </p>
            <Form onSubmit={handleSubmit} className="w-100">
              <div className="d-flex flex-column gap-2">
                {/* License plate and mileage */}
                <div className="d-flex gap-2">
                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="text"
                      name="licensePlate"
                      value={formData.licensePlate}
                      onChange={handleInputChange}
                      placeholder="License Plate"
                    />
                    <label>License Plate</label>
                  </Form.Floating>

                  <Form.Floating className="flex-grow-1">
                    <Form.Control
                      className="shadow-sm"
                      type="number"
                      name="mileage"
                      value={formData.mileage}
                      onChange={handleInputChange}
                      placeholder="Mileage (km)"
                    />
                    <label>Mileage (km)</label>
                  </Form.Floating>
                </div>

                {/* Tire size */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    type="text"
                    name="tireSize"
                    value={formData.tireSize}
                    onChange={handleInputChange}
                    placeholder="Tire Size"
                  />
                  <label>Tire Size</label>
                </Form.Floating>

                {/* Service history */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    as="textarea"
                    name="serviceHistory"
                    value={formData.serviceHistory}
                    onChange={handleInputChange}
                    placeholder="Service History"
                    style={{ height: '120px' }}
                  />
                  <label>Service History</label>
                </Form.Floating>

                {/* Preferred area */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    type="text"
                    name="preferredArea"
                    value={formData.preferredArea}
                    onChange={handleInputChange}
                    placeholder="Preferred Area"
                  />
                  <label>Preferred Area</label>
                </Form.Floating>

                {/* Contact phone */}
                <Form.Floating>
                  <Form.Control
                    className="shadow-sm"
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    placeholder="Contact Phone"
                  />
                  <label>Contact Phone</label>
                </Form.Floating>

                <Button type="submit" variant="primary" className="mt-2">
                  Update Vehicle Data
                </Button>
              </div>
            </Form>

            {/* Appointment simulation buttons */}
            <div className="mt-4">
              <h5 className="pb-0">Simulate Actions</h5>
              <div className="d-flex gap-2 mt-2">
                <Button
                  variant="outline-primary"
                  onClick={handleAppointmentScheduled}
                  disabled={appointmentScheduled}
                >
                  Simulate Appointment Scheduled
                </Button>
              </div>
            </div>
          </Col>

          {/* Columna centro: Imagen */}
          <Col md={2} className="d-flex p-4 pe-0 pb-0 align-items-end">
            {agentImage && (
              <div className="text-center mb-2">
                <img
                  src={agentImage}
                  alt={agentName}
                  className="img-fluid rounded"
                  style={{
                    width: '100%',
                    minWidth: '163px',
                    maxWidth: '163px',
                    height: 'auto'
                  }}
                />
              </div>
            )}
          </Col>

          {/* Right column: Agent image and conversation widget */}
          <Col md={5} className="p-4">
            <h5 className="pb-0 pt-2">Call with {agentName}</h5>
            <p className="fs-9 lh-sm mb-1">
              Press the button below to start a conversation with the agent. The
              agent will assist with scheduling service appointments or ordering
              tires.
            </p>
            {/* Conversation widget */}
            <HVAAyvensConversationWidget
              agentId={agentCode}
              agentName={agentName}
              onError={onError}
              onFeedback={addFeedbackMessage}
              firstMessage={firstMessage}
              prompt={prompt}
              voice={voice}
              language={language}
              vehicleInfo={vehicleInfo}
              onAppointmentScheduled={handleAppointmentScheduled}
              onServiceTypeSelected={handleServiceTypeSelected}
            />

            {renderFeedbackPanel()}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={handleCloseModal}>
          Close and reset
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVAAyvensModal;
